import { Injectable } from '@angular/core';
import {
  API_ENDPOINTS,
  formatDateToMinuteTimestamp,
} from '@g99-product-web-ui/shared-util-lib';
import { Observable } from 'rxjs';

import {
  IGiaAnalyticsDailyItem,
  IGiaAnalyticsUsageResponse,
  IUrlChangeHistoryListResponse,
  IUrlMetricsResponse,
  IUrlTrackingListResponse,
} from './gia-analytics.interface';
import { ServiceType } from '../../constants/service-types.enum';
import { HttpService } from '../../core/http-service/http.service';

@Injectable({
  providedIn: 'root',
})
export class GiaAnalyticsService {
  constructor(private readonly _http: HttpService) {}

  /**
   * @param startDate,endDate optional `yyyy-MM-dd` bounds. When both are given the server ignores
   * `filter` and reports on exactly that window, which is how the trailing 30-day default is built
   * — `filter` on its own only offers today / this week / this calendar month.
   */
  public getGiaAnalyticsUsage(
    filter = 'week',
    startDate?: string,
    endDate?: string
  ): Observable<IGiaAnalyticsUsageResponse> {
    const url = API_ENDPOINTS.AI_LUCY.ANALYTICS.giaUsage(
      filter,
      startDate,
      endDate
    );
    return this._http.get<IGiaAnalyticsUsageResponse>(
      url,
      {
        attachTenantId: true,
      },
      ServiceType.AI_SERVICE
    );
  }

  public aggregateTodayAnalytics(): Observable<string> {
    return this._http.post<string, object>(
      API_ENDPOINTS.AI_LUCY.ANALYTICS.aggregateToday,
      {},
      { attachTenantId: true },
      ServiceType.AI_SERVICE
    );
  }

  public getGiaAnalyticsDaily(days = 7): Observable<IGiaAnalyticsDailyItem[]> {
    return this._http.get<IGiaAnalyticsDailyItem[]>(
      API_ENDPOINTS.AI_LUCY.ANALYTICS.giaActivityDaily(days),
      { attachTenantId: true }
    );
  }

  public getUrlTrackingData(
    page: number,
    size: number
  ): Observable<IUrlTrackingListResponse> {
    const url = `${API_ENDPOINTS.AI_LUCY.ANALYTICS.urlTracking}?page=${page}&size=${size}`;
    return this._http.get<IUrlTrackingListResponse>(
      url,
      { attachTenantId: true },
      ServiceType.AI_SERVICE
    );
  }

  /**
   * Change history of the Gia booking URL for the current tenant, newest change first.
   */
  public getBookingUrlHistory(
    page: number,
    size: number
  ): Observable<IUrlChangeHistoryListResponse> {
    const url = `${API_ENDPOINTS.AI_LUCY.ANALYTICS.bookingUrlHistory}?page=${page}&size=${size}`;
    return this._http.get<IUrlChangeHistoryListResponse>(
      url,
      { attachTenantId: true },
      ServiceType.AI_SERVICE
    );
  }

  public fetchTrackingMetrics(
    id: number,
    startDate: string | Date,
    endDate: string | Date
  ): Observable<IUrlMetricsResponse> {
    // Backend binds these via @DateTimeFormat(pattern = "yyyy-MM-dd HH:mm") —
    // a seconds component (or a 'T'/'Z' ISO separator) is unparsed text and yields a 400.
    const start = startDate instanceof Date ? startDate : new Date(startDate);
    const end = endDate instanceof Date ? endDate : new Date(endDate);
    const url = `${API_ENDPOINTS.AI_LUCY.ANALYTICS.urlTrackingMetrics(id)}&startDate=${encodeURIComponent(formatDateToMinuteTimestamp(start))}&endDate=${encodeURIComponent(formatDateToMinuteTimestamp(end))}`;
    return this._http.get<IUrlMetricsResponse>(
      url,
      { attachTenantId: true },
      ServiceType.AI_SERVICE
    );
  }
}
